// To our knowledge, there's no way to tell from EXIF
// (or at least the EXIF we get from exif.js) which image
// contains RGB and which contains thermal data, so we're
// left with relying on the filename conventions to
// figure this out. "_V" or "_W" means RGB, "_T" means thermal.
export const djiThermalImageRgbPattern = new RegExp(
  '.*_[VW][^A-Za-z0-9]*.*\\.JPG$'
)
export const djiThermalImageThermalPattern = new RegExp(
  '.*_T[^A-Za-z0-9]*.*\\.JPG$'
)

const djiThermal = {
  bands: context => {
    const hasRgb = context.images.some(image =>
      djiThermalImageRgbPattern.exec(image.filename)
    )
    const hasThermal = context.images.some(image =>
      djiThermalImageThermalPattern.exec(image.filename)
    )
    return `${hasRgb ? 'rgb' : ''}${hasThermal ? 't' : ''}`
  },
  imagesPerCapture: context => {
    const hasRgb = context.images.some(image =>
      djiThermalImageRgbPattern.exec(image.filename)
    )
    const hasThermal = context.images.some(image =>
      djiThermalImageThermalPattern.exec(image.filename)
    )
    return (hasRgb ? 1 : 0) + (hasThermal ? 1 : 0)
  },
  hasDls: false,
  sensor: [6.4, 4.8],
}

const db2VisionLaQuinta8mm = {
  bands: 'rgbna',
  imagesPerCapture: 1,
}

export const GENERIC_RGB_CAMERA = {
  camera: 'Generic RGB',
  bands: 'rgb',
  imagesPerCapture: 1,
}

// See https://en.wikipedia.org/wiki/Image_sensor_format#Table_of_sensor_formats_and_sizes
const defaultSensors = {
  '1/2.3"': [6.17, 4.55],
  '1"': [13.2, 8.8],
}

export const CAMERAS = {
  'Hasselblad L1D-20c': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1"'],
  },
  'DJI FC6310': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1"'],
  },
  'DJI FC6510': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1"'],
  },
  'DJI FC6520': {
    ...GENERIC_RGB_CAMERA,
    sensor: [17.3, 13],
  },
  'DJI FC6540': {
    ...GENERIC_RGB_CAMERA,
    sensor: [23.5, 15.7],
  },
  'DJI ZenmuseP1': {
    ...GENERIC_RGB_CAMERA,
    sensor: [35.9, 24],
  },
  'DJI FC300S': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1/2.3"'],
  },
  'DJI FC220': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1/2.3"'],
  },
  'DJI FC6310R': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1"'],
  },
  'DJI FC330': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1/2.3"'],
  },
  'DJI FC7203': {
    ...GENERIC_RGB_CAMERA,
    sensor: defaultSensors['1/2.3"'],
  },
  'DJI FC6360': {
    bands: fromFileType({ tif: 'bgren', jpg: 'rgb' }),
    imagesPerCapture: fromFileType({ tif: numberBands, jpg: 1 }),
    hasDls: fromFileType({ tif: true, jpg: false }),
    focalLength: 5.74,
    sensor: [4.8, 4.0],
  },
  'DJ FC636': {
    bands: fromFileType({ tif: 'bgren', jpg: 'rgb' }),
    imagesPerCapture: fromFileType({ tif: numberBands, jpg: 1 }),
    hasDls: fromFileType({ tif: true, jpg: false }),
    focalLength: 5.74,
    sensor: [4.8, 4.0],
  },
  'DJI M30T': djiThermal,
  'DJI M3T': djiThermal,
  'DJI ZH20T': { ...djiThermal, sensor: [6.3, 4.72] },
  'DJI M3M': {
    bands: fromFileType({ tif: 'gren', jpg: 'rgb' }),
    imagesPerCapture: fromFileType({ tif: numberBands, jpg: 1 }),
    hasDls: fromFileType({ tif: true, jpg: false }),
    sensor: [17.3, 13],
  },
  'Parrot Sequoia': {
    bands: fromFileType({ tif: 'gren', jpg: 'rgb' }),
    imagesPerCapture: fromFileType({ tif: numberBands, jpg: 1 }),
    hasDls: fromFileType({ tif: true, jpg: false }),
  },
  'MicaSense Altum': {
    bands: 'bgrent',
    imagesPerCapture: numberBands,
    hasDls: fromFileType({ tif: true, jpg: false }),
    focalLength: 8,
    sensor: [7.121, 5.327],
  },
  'MicaSense Altum-PT': {
    bands: 'bgrentP',
    imagesPerCapture: numberBands,
    hasDls: fromFileType({ tif: true, jpg: false }),
  },
  'MicaSense RedEdge': {
    bands: 'bgren',
    imagesPerCapture: numberBands,
    hasDls: fromFileType({ tif: true, jpg: false }),
    focalLength: 5.4,
    sensor: [4.8, 3.6],
  },
  'MicaSense RedEdge-M': {
    bands: fromNumberUniqueBands({ 5: 'bgren', 10: 'bBgGrReEnN' }),
    imagesPerCapture: numberBands,
    hasDls: fromFileType({ tif: true, jpg: false }),
  },
  'MicaSense RedEdge-P': {
    bands: fromNumberUniqueBands({ 6: 'bgrenP' }),
    imagesPerCapture: numberBands,
    hasDls: fromFileType({ tif: true, jpg: false }),
  },
  'Sentera NarrowNDVI_1.2MP-GS-0002_4.14mm-0001_0015': {
    bands: 'rgn',
    imagesPerCapture: 1,
  },
  'SAL & Eoptis MAIA': { bands: 'vbgreEnNi', imagesPerCapture: 1 },
  'MAPIR Survey3N_RGN': { bands: 'rgn', imagesPerCapture: 1 },
  'SlantRange SlantRange-4P+': {
    bands: 'rgbRen',
    imagesPerCapture: numberBands,
  },
  'Sentera 21214-00_3.2MP-GS-0001': { bands: 'bgren', imagesPerCapture: 1 },
  'Sentera 21214-01_3.2MP-GS-0001': {
    bands: 'bgren',
    imagesPerCapture: numberBands,
  },
  'Sentera 21244-00_3.2MP-GS-0001': { bands: 'bgren', imagesPerCapture: 1 },
  'Sentera 21244-01_3.2MP-GS-0001': {
    bands: 'bgren',
    imagesPerCapture: numberBands,
  },
  'Autel Robotics XT705': {
    bands: 'rgba',
    imagesPerCapture: 1,
    sensor: [6.4, 4.8],
  },
  'DB2-Visio LaQuinta  8mm  1.0': db2VisionLaQuinta8mm,
  'DB2-Vision LaQuinta  8mm  1.06': db2VisionLaQuinta8mm,
  'DB2-Vision LaQuinta  8mm  1.08': db2VisionLaQuinta8mm,
  'DB2-Vision LaQuinta M-Spectral': db2VisionLaQuinta8mm,
  // "Cameras" that are really drone makes and will not match from EXIF
  // these are used from the flight planner, but should ideally be listed
  // with the real camera names (and correct bands etc.)
  'Phantom 3': {
    focalLength: 3.61,
    sensor: [6.2, 4.7],
  },
  'Phantom 4': {
    focalLength: 3.61,
    sensor: [6.2, 4.7],
  },
  'Phantom 4 Pro Camera': {
    focalLength: 8.8,
    sensor: defaultSensors['1"'],
  },
  'Phantom 4 Pro V2 Camera': {
    focalLength: 8.8,
    sensor: defaultSensors['1"'],
  },
  'Phantom 4 Advanced Camera': {
    focalLength: 8.8,
    sensor: defaultSensors['1"'],
  },
  'Phantom 4 RTK Camera': {
    focalLength: 8.8,
    sensor: defaultSensors['1"'],
  },
  'Phantom 4 Multispectral Camera': {
    focalLength: 5.74,
    sensor: [4.8, 4.0],
  },
  'Mavic Pro': {
    focalLength: 4.73,
    sensor: [6.2, 4.7],
  },
  'Mavic 2 Pro': {
    focalLength: 10.3,
    sensor: defaultSensors['1"'],
  },
}

const FILE_TYPES = {
  tif: 'tif',
  tiff: 'tif',
  jpg: 'jpg',
  jpeg: 'jpg',
}

/**
 * Get camera properties for a list of images
 * @param {array} images
 * @param {object} options
 * @param {boolean} options.allowMultipleCameras if not set, an exception will be thrown if more than one type of camera is used
 */
export function getCameraProperties(images, options = {}) {
  if (!images || !Array.isArray(images) || images.length < 1) {
    throw new Error('Missing images.')
  }

  const make = images[0].make
  const model = images[0].model

  const takenWithSameCamera = images.every(
    image => image.model === model && image.make === make
  )

  if (!takenWithSameCamera) {
    if (!options.allowMultipleCameras) {
      throw new Error('More than one camera type used.')
    } else {
      return { ...GENERIC_RGB_CAMERA, multipleCameras: true }
    }
  }

  const camera = getCamera(make, model)
  const fileExtension = images[0].filename.split('.').pop().toLowerCase()

  const cameraDef = CAMERAS[camera]

  if (cameraDef) {
    const properties = { camera }
    const context = { images, fileExtension }
    const evaluateProperty = attribute =>
      (properties[attribute] = evaluate(
        cameraDef[attribute],
        context,
        properties
      ))

    // Evaluate some properties in pre-defined order
    evaluateProperty('bands')
    evaluateProperty('imagesPerCapture')

    // Evaluate remaining properties
    for (const attribute in cameraDef) {
      if (!properties[attribute]) {
        evaluateProperty(attribute)
      }
    }

    return properties
  } else {
    // Assume generic RGB
    return GENERIC_RGB_CAMERA
  }
}

function evaluate(v, context, properties) {
  if (isFunction(v)) {
    return v(context, properties)
  } else {
    return v
  }

  function isFunction(x) {
    return typeof x === 'function'
  }
}

function fromFileType(mapping) {
  return function (context, properties) {
    const { fileExtension } = context
    const fileType = FILE_TYPES[fileExtension] || fileExtension
    if (mapping[fileType] !== undefined) {
      return evaluate(mapping[fileType], context, properties)
    } else {
      throw new Error(`Unexpected file type ${fileType}.`)
    }
  }
}

function fromNumberUniqueBands(mapping) {
  return function ({ images }) {
    const uniqueBands = getUniqueBands(images)
    if (mapping[uniqueBands] !== undefined) {
      return mapping[uniqueBands]
    } else {
      throw new Error(`Unexpected number of unique bands: ${uniqueBands}.`)
    }
  }
}

function numberBands(_, properties) {
  return properties.bands.length
}

export function getUniqueBands(images) {
  return new Set(images.map(image => image.bandName)).size
}

function getCamera(make, model) {
  return `${make} ${model}`
}
